import { useCallback, useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/api";

// Bounded acknowledged pipeline. Inputs are never replayed after reconnect.
export function usePcControl(open, agentId) {
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState("Desconectado");
  const [latency, setLatency] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const transport = useRef(null);
  const retryCount = useRef(0);
  const send = useCallback(event => transport.current?.send(event) || false, []);
  const move = useCallback((dx, dy) => transport.current?.move(dx, dy), []);
  const reconnect = useCallback(() => { retryCount.current = 0; setAttempt(n => n + 1); }, []);
  useEffect(() => {
    setReady(false); setLatency(null);
    if (!open) return;
    if (!agentId) { setStatus("Tu agente Windows está desconectado"); return; }
    const url = new URL(BACKEND_URL || window.location.origin);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = "/api/pc/control/" + encodeURIComponent(agentId);
    url.searchParams.set("protocol", "2");
    const ws = new WebSocket(url.toString());
    let acknowledged = false, events = [], closed = false, retryTimer = null;
    const inFlight = new Map();
    let windowSize = 1, sequence = 0;
    let peer = null, direct = null, directActive = false, rtcTimer = null, lastMotionSend = -100;
    const stopDirect = () => {
      clearTimeout(rtcTimer);
      if (direct) { direct.onclose = null; direct.onerror = null; direct.close(); }
      if (peer) { peer.onconnectionstatechange = null; peer.close(); }
      direct = null; peer = null; directActive = false;
    };
    let motion = { dx: 0, dy: 0, at: 0 };
    let error = "Sesión cerrada. Tocá Reconectar para volver.";
    setStatus("Conectando con tu PC…");
    const close = message => { error = message; ws.close(); };
    const flush = () => {
      if (!acknowledged || closed || inFlight.size >= windowSize || ws.readyState !== WebSocket.OPEN) return;
      if (!directActive && direct?.readyState === "open" && !inFlight.size) {
        directActive = true; setStatus("Conectado con tu PC · Wi-Fi directo");
      }
      let event = events.shift();
      if (event && performance.now() - event.at > 1000) {
        events = []; close("La conexión se demoró; se descartaron las entradas. Reconectá."); return;
      }
      if (!event && performance.now() - lastMotionSend < (directActive ? 16 : 40)) return;
      if (!event && performance.now() - motion.at < 500) {
        const dx = Math.max(-500, Math.min(500, Math.trunc(motion.dx)));
        const dy = Math.max(-500, Math.min(500, Math.trunc(motion.dy)));
        if (dx || dy) { event = { action: "move", dx, dy }; motion.dx -= dx; motion.dy -= dy; lastMotionSend = performance.now(); }
      }
      if (!event) return;
      const { at, ...payload } = event;
      const id = ++sequence;
      inFlight.set(id, performance.now());
      const wire = JSON.stringify(windowSize > 1 ? { ...payload, sequence: id } : payload);
      if (directActive) direct.send(wire); else ws.send(wire);
    };
    const channel = {
      send(event) {
        if (!acknowledged || closed || ws.readyState !== WebSocket.OPEN) return false;
        if (events.length >= 12) {
          close("Demasiadas entradas pendientes. Se liberó el control."); return false;
        }
        const dx = Math.max(-500, Math.min(500, Math.trunc(motion.dx)));
        const dy = Math.max(-500, Math.min(500, Math.trunc(motion.dy)));
        if ((dx || dy) && performance.now() - motion.at < 500)
          events.push({ action: "move", dx, dy, at: performance.now() });
        motion = { dx: 0, dy: 0, at: 0 };
        events.push({ ...event, at: performance.now() });
        flush(); return true;
      },
      move(dx, dy) {
        if (!acknowledged || closed) return;
        motion.dx += dx; motion.dy += dy; motion.at = performance.now();
      }
    };
    transport.current = channel;
    const interval = setInterval(() => {
      if (inFlight.size && performance.now() - inFlight.values().next().value > 4000) {
        close("Tu PC dejó de responder. Reconectá para continuar."); return;
      }
      flush();
    }, 16);
    const heartbeat = setInterval(() => {
      if (!inFlight.size && !events.length && !(Math.trunc(motion.dx) || Math.trunc(motion.dy)))
        channel.send({ action: "keepalive" });
    }, 1000);
    const connectionTimeout = setTimeout(() => {
      if (!acknowledged) close("No se pudo abrir la sesión. Revisá la conexión y reconectá.");
    }, 10000);
    const startDirect = async () => {
      if (!window.RTCPeerConnection || closed) return;
      try {
        const candidate = new RTCPeerConnection({ iceServers: [] });
        peer = candidate;
        direct = candidate.createDataChannel("jarvis-control", { ordered: true });
        direct.onmessage = event => receive(event);
        direct.onopen = () => { clearTimeout(rtcTimer); flush(); };
        direct.onclose = () => {
          if (directActive) close("El enlace Wi-Fi se interrumpió. Reconectá; no se repitieron las entradas pendientes.");
        };
        const fallback = () => {
          if (directActive) { close("El enlace Wi-Fi se interrumpió. Reconectá."); return; }
          stopDirect();
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({type:"rtc_close"}));
        };
        candidate.onconnectionstatechange = () => { if (candidate.connectionState === "failed") fallback(); };
        rtcTimer = setTimeout(fallback, 8000);
        await candidate.setLocalDescription(await candidate.createOffer());
        if (candidate.iceGatheringState !== "complete") await new Promise(resolve => {
          const timer = setTimeout(resolve, 2500);
          const done = () => { if (candidate.iceGatheringState === "complete") { clearTimeout(timer); candidate.removeEventListener("icegatheringstatechange",done); resolve(); } };
          candidate.addEventListener("icegatheringstatechange",done);
        });
        if (!closed && peer === candidate && ws.readyState === WebSocket.OPEN)
          ws.send(JSON.stringify({type:"rtc_offer",sdp:candidate.localDescription.sdp}));
      } catch { stopDirect(); }
    };
    const lease = setInterval(() => {
      if (peer && acknowledged && !closed && ws.readyState === WebSocket.OPEN)
        ws.send(JSON.stringify({type:"rtc_lease"}));
    }, 500);
    const receive = event => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === "rtc_answer") {
          peer?.setRemoteDescription({type:"answer",sdp:message.sdp}).catch(()=>stopDirect());
          return;
        }
        if (message.type === "rtc_unavailable") { stopDirect(); return; }
        if (message.type === "ready") {
          acknowledged = true; retryCount.current = 0; clearTimeout(connectionTimeout);
          windowSize = Number.isInteger(message.max_in_flight) ? Math.max(1, Math.min(6, message.max_in_flight)) : 1;
          setReady(true); setStatus("Conectado con tu PC");
          if (message.direct_local) startDirect();
        } else if (message.ok) {
          const id = windowSize > 1 ? message.sequence : inFlight.keys().next().value;
          if (!inFlight.has(id)) { close("Confirmación inválida del servidor"); return; }
          setLatency(Math.round(performance.now() - inFlight.get(id)));
          inFlight.delete(id);
        } else {
          close(message.error || "Windows rechazó la entrada"); return;
        }
        flush();
      } catch { close("Respuesta inválida del servidor. Reconectá."); }
    };
    ws.onmessage = receive;
    ws.onerror = () => { error = "No se pudo conectar con el servidor de JARVIS"; };
    ws.onclose = event => {
      closed = true; acknowledged = false; inFlight.clear(); events = []; stopDirect();
      motion = { dx: 0, dy: 0, at: 0 }; setReady(false);
      if (event?.code === 1008 && error.startsWith("Sesión cerrada"))
        error = "Sesión o permisos rechazados. Revisá tu acceso a JARVIS.";
      if (event?.code !== 1008 && !document.hidden && retryCount.current < 5) {
        const delay = Math.min(16000, 1000 * (2 ** retryCount.current++));
        setStatus("Reconectando automáticamente…");
        retryTimer = setTimeout(() => setAttempt(n => n + 1), delay);
      } else setStatus(error);
    };
    const hide = () => { if (document.hidden) close("Sesión pausada al salir. Tocá Reconectar."); };
    const blur = () => channel.send({ action: "release" });
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("blur", blur);
    return () => {
      clearInterval(interval); clearInterval(heartbeat); clearInterval(lease); clearTimeout(connectionTimeout); clearTimeout(retryTimer); stopDirect();
      document.removeEventListener("visibilitychange", hide); window.removeEventListener("blur", blur);
      closed = true; events = []; ws.onclose = null; ws.close();
      if (transport.current === channel) transport.current = null;
    };
  }, [open, agentId, attempt]);
  return { ready, status, latency, send, move, reconnect };
}
